/**
 * Sección "Regularizaciones" de Asistencia (007 · T036 y T056; contracts/ui.md §1.3 y §1.5, spec
 * US1-2, US3 y US4-3).
 *
 * Solo aparece si hay algo que mostrar. Una fila por ítem, en el orden del servidor, con el día,
 * qué falta y el estado (badge icono + texto):
 *
 * | Estado | Detalle | Acción |
 * |---|---|---|
 * | `SUGGESTED` | la marca existente | "Regularizar" |
 * | `ASKED` | la nota del pedido | "Regularizar" |
 * | `PENDING` | "Salida 18:00 propuesta" | "Cancelar solicitud" (ghost → hoja) |
 * | `APPROVED` | "Salida 18:30 (propusiste 18:00)" + nota | — |
 * | `REJECTED` | el motivo | "Proponer otra hora" si `can_request`, si no "Habla con tu administrador." |
 * | `CANCELLED` | la causa | "Regularizar" si `can_request` |
 *
 * Las acciones salen **solo** de `can_request` (el servidor) y de `readOnly` (empresa suspendida:
 * mismos estados, sin acciones, FR-024). Sin red, las acciones quedan deshabilitadas. Cancelar no
 * es optimista: se espera la respuesta y se invalidan las vistas; un `409 REGULARIZATION_NOT_OPEN`
 * cierra la hoja y avisa "Esta solicitud ya no está en revisión.".
 *
 * `id="regularizaciones"`: destino del enlace "y N más" del Inicio (`/asistencia#regularizaciones`).
 */
import { Link, useLocation } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import type { StaffRegularizationItem } from '@fermin/contracts';

import { ApiError } from '../../api/client';
import { Button, buttonClasses } from '../../components/ui/button';
import { formatTime, t } from '../../i18n';

import { useCancelRegularization } from './api';
import { CancelSheet } from './cancel-sheet';
import { anchorSummary, missingNoun, shortDay, typeLabel } from './format';
import { StateBadge } from './state-badge';

export const REGULARIZATIONS_SECTION_ID = 'regularizaciones';

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Líneas de detalle de la fila según el estado (textos es-PE, horas 24 h). */
function detailLines(item: StaffRegularizationItem): string[] {
  const request = item.request;
  const type = typeLabel(item.missing_type);
  switch (item.state) {
    case 'SUGGESTED':
      return [anchorSummary(item.anchor)];
    case 'ASKED':
      return request?.ask_note
        ? [anchorSummary(item.anchor), t('regularization.section.askedNote', { note: request.ask_note })]
        : [anchorSummary(item.anchor)];
    case 'PENDING':
      return request?.proposed
        ? [t('regularization.section.proposed', { type, time: request.proposed.time })]
        : [anchorSummary(item.anchor)];
    case 'APPROVED': {
      const lines: string[] = [];
      if (request?.approved_timestamp) {
        const approved = formatTime(request.approved_timestamp, item.anchor.site_timezone);
        const proposed = request.proposed?.time ?? null;
        lines.push(
          proposed !== null && proposed !== approved
            ? t('regularization.section.approvedAdjusted', { type, time: approved, proposed })
            : t('regularization.section.approved', { type, time: approved }),
        );
      }
      if (request?.resolution_note) {
        lines.push(t('regularization.section.adminNote', { note: request.resolution_note }));
      }
      return lines;
    }
    case 'REJECTED':
      return request?.resolution_note
        ? [t('regularization.section.rejectedReason', { reason: request.resolution_note })]
        : [];
    case 'CANCELLED':
      return request?.cancel_reason ? [t(`regularization.cancelReasons.${request.cancel_reason}`)] : [];
  }
}

interface RowProps {
  item: StaffRegularizationItem;
  readOnly: boolean;
  online: boolean;
  onCancel: (item: StaffRegularizationItem) => void;
}

function RegularizationRow({ item, readOnly, online, onCancel }: RowProps) {
  const title = capitalize(
    t('regularization.section.itemTitle', {
      missing: missingNoun(item.missing_type),
      day: shortDay(item.anchor.operative_day),
    }),
  );
  const canAct = !readOnly && item.can_request;
  const canCancel = !readOnly && item.state === 'PENDING' && item.request !== null;
  const actionLabel =
    item.state === 'REJECTED'
      ? t('regularization.section.retry')
      : t('regularization.section.regularize');
  const talkToAdmin = !canAct && item.state === 'REJECTED';

  return (
    <li
      data-testid="regularization-item"
      data-state={item.state}
      data-mark-id={item.anchor.mark_id}
      className="flex flex-col gap-fm-2 py-fm-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-fm-2">
        <span className="font-ui text-fm-base font-semibold text-fm-text">{title}</span>
        <StateBadge state={item.state} />
      </div>
      {detailLines(item).map((line) => (
        <p key={line} data-testid="regularization-detail" className="text-fm-sm text-fm-text-secondary">
          {line}
        </p>
      ))}
      {talkToAdmin ? (
        <p data-testid="regularization-talk-to-admin" className="text-fm-sm text-fm-text-secondary">
          {t('regularization.section.talkToAdmin')}
        </p>
      ) : null}
      {canAct ? (
        online ? (
          <Link
            to="/regularizar/$markId"
            params={{ markId: item.anchor.mark_id }}
            data-testid="regularization-action"
            className={buttonClasses({ variant: 'secondary', size: 'md', fullWidth: false })}
          >
            {actionLabel}
          </Link>
        ) : (
          <Button
            data-testid="regularization-action"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled
          >
            {t('regularization.form.offline')}
          </Button>
        )
      ) : null}
      {canCancel ? (
        <Button
          data-testid="regularization-cancel"
          variant="ghost"
          size="md"
          fullWidth={false}
          className="self-start"
          disabled={!online}
          onClick={() => onCancel(item)}
        >
          {t('regularization.section.cancel')}
        </Button>
      ) : null}
    </li>
  );
}

export interface RegularizationsSectionProps {
  items: readonly StaffRegularizationItem[];
  /** Empresa suspendida: mismos estados, sin acciones (FR-024). */
  readOnly: boolean;
  online: boolean;
}

export function RegularizationsSection({ items, readOnly, online }: RegularizationsSectionProps) {
  const cancel = useCancelRegularization();
  const [target, setTarget] = useState<StaffRegularizationItem | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const hash = useLocation({ select: (location) => location.hash });

  // "y N más" del Inicio llega con `#regularizaciones`: la sección se pinta cuando llegan los
  // datos, después de que el router intentara desplazarse; se hace aquí.
  useEffect(() => {
    if (hash === REGULARIZATIONS_SECTION_ID) {
      sectionRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [hash]);

  if (items.length === 0) {
    return null;
  }

  const closeSheet = () => {
    if (!cancel.isPending) {
      setTarget(null);
      setCancelError(null);
    }
  };

  const confirmCancel = () => {
    const requestId = target?.request?.id;
    if (!requestId) {
      return;
    }
    setCancelError(null);
    cancel.mutate(requestId, {
      onSuccess: () => {
        setTarget(null);
        setNotice(null);
      },
      onError: (error) => {
        if (error instanceof ApiError && (error.is('REGULARIZATION_NOT_OPEN') || error.is('NOT_FOUND'))) {
          setTarget(null);
          setNotice(t('regularization.section.notOpen'));
          return;
        }
        setCancelError(t('regularization.cancelSheet.error'));
      },
    });
  };

  return (
    <section
      ref={sectionRef}
      id={REGULARIZATIONS_SECTION_ID}
      data-testid="regularizations-section"
      aria-labelledby="regularizations-section-title"
      className="flex scroll-mt-fm-4 flex-col gap-fm-2 rounded-fm-lg border border-fm-border bg-fm-surface p-fm-4 shadow-fm-1"
    >
      <h2
        id="regularizations-section-title"
        className="font-display text-fm-lg font-bold text-fm-text"
      >
        {t('regularization.section.title')}
      </h2>
      {readOnly ? (
        <p data-testid="regularizations-read-only" className="text-fm-sm text-fm-text-secondary">
          {t('regularization.section.readOnly')}
        </p>
      ) : null}
      {notice !== null ? (
        <p data-testid="regularizations-notice" role="status" className="text-fm-base text-fm-text">
          {notice}
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-fm-border">
        {items.map((item) => (
          <RegularizationRow
            key={item.anchor.mark_id}
            item={item}
            readOnly={readOnly}
            online={online}
            onCancel={(selected) => {
              setCancelError(null);
              setTarget(selected);
            }}
          />
        ))}
      </ul>
      <CancelSheet
        open={target !== null}
        pending={cancel.isPending}
        error={cancelError}
        onConfirm={confirmCancel}
        onClose={closeSheet}
      />
    </section>
  );
}
